/**
 * NiV Support Page - Support Ticket Management
 *
 * Admin page for managing support tickets from all therapists.
 * Features:
 * - View all tickets
 * - Filter by status
 * - Chat with therapists
 * - Change ticket status
 * - Delete tickets
 */

import { useState, useEffect, useRef, useMemo } from "react";
import {
  useGetAllSupportTicketsQuery,
  useAddAdminMessageMutation,
  useUpdateTicketStatusMutation,
  useDeleteTicketMutation,
} from "../../../store/services/nivSupportApi";
import {
  STATUS_LABELS,
  STATUS_COLORS,
  SUPPORT_TICKET_STATUSES,
  formatTicketDate,
} from "../../../services/supportFirebase";
import { useToast } from "../../../contexts/ToastContext";
import {
  Headset,
  MagnifyingGlass,
  PaperPlaneTilt,
  ArrowLeft,
  CheckCircle,
  Clock,
  Warning,
  Info,
  Trash,
  Funnel,
  ChatCircleDots,
  Buildings,
  User,
} from "@phosphor-icons/react";
import ContentLoader from "../../../components/shared/ContentLoader";
import LoaderLoop from "../../../components/shared/loader/LoaderLoop";
import LennyReviewQueue from "./support/LennyReviewQueue";
import "./NivSupportPage.css";

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_OPTIONS = [
  { value: "all", label: "הכל" },
  { value: SUPPORT_TICKET_STATUSES.OPEN, label: STATUS_LABELS.open },
  { value: SUPPORT_TICKET_STATUSES.WAITING_ADMIN, label: STATUS_LABELS.waiting_admin },
  { value: SUPPORT_TICKET_STATUSES.WAITING_CUSTOMER, label: STATUS_LABELS.waiting_customer },
  { value: SUPPORT_TICKET_STATUSES.IN_PROGRESS, label: STATUS_LABELS.in_progress },
  { value: SUPPORT_TICKET_STATUSES.STUCK, label: STATUS_LABELS.stuck },
  { value: SUPPORT_TICKET_STATUSES.COMPLETED, label: STATUS_LABELS.completed },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function NivSupportPage({ initialTicketId = null, embedded = false }) {
  const { showSuccess, showError } = useToast();

  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  const chatEndRef = useRef(null);

  // API
  const {
    data: tickets = [],
    isLoading: ticketsLoading,
    refetch: refetchTickets,
  } = useGetAllSupportTicketsQuery();

  const [addAdminMessage, { isLoading: sending }] = useAddAdminMessageMutation();
  const [updateTicketStatus, { isLoading: updatingStatus }] = useUpdateTicketStatusMutation();
  const [deleteTicket, { isLoading: deleting }] = useDeleteTicketMutation();

  // Deep-link from the briefing ("ממתינים לתשובתך" card) — open once tickets land
  const openedInitialRef = useRef(null);
  useEffect(() => {
    if (!initialTicketId || openedInitialRef.current === initialTicketId) return;
    const match = tickets.find((t) => t.id === initialTicketId);
    if (match) {
      openedInitialRef.current = initialTicketId;
      setSelectedTicket(match);
    }
  }, [initialTicketId, tickets]);

  // Scroll to bottom of chat when messages change
  useEffect(() => {
    if (selectedTicket && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedTicket?.messages]);

  // Filter tickets
  const filteredTickets = useMemo(() => {
    let result = [...tickets];

    // Filter by status
    if (statusFilter !== "all") {
      result = result.filter((t) => t.status === statusFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.subject?.toLowerCase().includes(query) ||
          t.therapistName?.toLowerCase().includes(query) ||
          t.clinicName?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [tickets, statusFilter, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    return {
      total: tickets.length,
      open: tickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.OPEN).length,
      waitingAdmin: tickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.WAITING_ADMIN).length,
      waitingCustomer: tickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.WAITING_CUSTOMER).length,
      inProgress: tickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.IN_PROGRESS).length,
      stuck: tickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.STUCK).length,
      completed: tickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.COMPLETED).length,
    };
  }, [tickets]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleBackToList = () => {
    setSelectedTicket(null);
    setNewMessage("");
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedTicket) return;

    try {
      const updatedTicket = await addAdminMessage({
        ticketId: selectedTicket.id,
        content: newMessage,
        clinicId: selectedTicket.clinicId,
        therapistId: selectedTicket.therapistId,
      }).unwrap();

      setNewMessage("");
      setSelectedTicket(updatedTicket);
      refetchTickets();
      showSuccess("ההודעה נשלחה והמטפל יקבל התראה");
    } catch (error) {
      console.error("Error sending message:", error);
      showError("שגיאה בשליחת ההודעה");
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return;

    try {
      const updatedTicket = await updateTicketStatus({
        ticketId: selectedTicket.id,
        status: newStatus,
        clinicId: selectedTicket.clinicId,
        therapistId: selectedTicket.therapistId,
        shouldNotify: newStatus === SUPPORT_TICKET_STATUSES.COMPLETED,
      }).unwrap();

      setSelectedTicket(updatedTicket);
      refetchTickets();
      
      if (newStatus === SUPPORT_TICKET_STATUSES.COMPLETED) {
        showSuccess("הטיקט נסגר והמטפל יקבל התראה");
      } else {
        showSuccess("הסטטוס עודכן");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showError("שגיאה בעדכון הסטטוס");
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    try {
      await deleteTicket({ ticketId }).unwrap();
      setShowDeleteConfirm(null);
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(null);
      }
      refetchTickets();
      showSuccess("הטיקט נמחק");
    } catch (error) {
      console.error("Error deleting ticket:", error);
      showError("שגיאה במחיקת הטיקט");
    }
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const getStatusIcon = (status) => {
    switch (status) {
      case SUPPORT_TICKET_STATUSES.COMPLETED:
        return <CheckCircle size={16} weight="fill" />;
      case SUPPORT_TICKET_STATUSES.STUCK:
        return <Warning size={16} weight="fill" />;
      case SUPPORT_TICKET_STATUSES.WAITING_ADMIN:
        return <Warning size={16} weight="fill" />;
      default:
        return <Clock size={16} weight="fill" />;
    }
  };

  // ============================================================================
  // RENDER: TICKETS LIST
  // ============================================================================

  const renderTicketsList = () => (
    <div className="niv-support-list">
      {/* Lenny (AI support) exchanges flagged for a human look */}
      <LennyReviewQueue />

      {/* Stats */}
      <div className="niv-support-stats">
        <div className="niv-support-stat">
          <span className="niv-support-stat-value">{stats.total}</span>
          <span className="niv-support-stat-label">סה"כ</span>
        </div>
        <div className="niv-support-stat urgent">
          <span className="niv-support-stat-value">{stats.waitingAdmin}</span>
          <span className="niv-support-stat-label">ממתינים לתגובה</span>
        </div>
        <div className="niv-support-stat">
          <span className="niv-support-stat-value">{stats.open + stats.inProgress}</span>
          <span className="niv-support-stat-label">פתוחים</span>
        </div>
        <div className="niv-support-stat success">
          <span className="niv-support-stat-value">{stats.completed}</span>
          <span className="niv-support-stat-label">הסתיימו</span>
        </div>
      </div>

      {/* Filters */}
      <div className="niv-support-filters">
        <div className="niv-support-search">
          <MagnifyingGlass size={18} />
          <input
            type="text"
            placeholder="חיפוש לפי נושא, מטפל או קליניקה..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="niv-support-filter">
          <Funnel size={18} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tickets */}
      {ticketsLoading ? (
        <ContentLoader message="טוען טיקטים..." />
      ) : filteredTickets.length === 0 ? (
        <div className="niv-support-empty">
          <ChatCircleDots size={48} weight="light" />
          <h4>אין טיקטים</h4>
          <p>{statusFilter !== "all" ? "נסה לשנות את הסינון" : "אין טיקטי תמיכה פתוחים"}</p>
        </div>
      ) : (
        <div className="niv-support-tickets">
          {filteredTickets.map((ticket) => (
            <div
              key={ticket.id}
              className={`niv-support-ticket ${
                ticket.status === SUPPORT_TICKET_STATUSES.WAITING_ADMIN
                  ? "urgent"
                  : ""
              } ${selectedTicket?.id === ticket.id ? "selected" : ""}`}
              onClick={() => handleSelectTicket(ticket)}
            >
              <div className="niv-support-ticket-header">
                <h4>{ticket.subject}</h4>
                <span
                  className="niv-support-ticket-status"
                  style={{
                    backgroundColor: `${STATUS_COLORS[ticket.status]}20`,
                    color: STATUS_COLORS[ticket.status],
                  }}
                >
                  {getStatusIcon(ticket.status)}
                  {STATUS_LABELS[ticket.status]}
                </span>
              </div>
              <div className="niv-support-ticket-info">
                <span className="niv-support-ticket-therapist">
                  <User size={14} />
                  {ticket.therapistName}
                </span>
                <span className="niv-support-ticket-clinic">
                  <Buildings size={14} />
                  {ticket.clinicName}
                </span>
              </div>
              <div className="niv-support-ticket-meta">
                <span>{formatTicketDate(ticket.createdAt)}</span>
                <span>{ticket.messages?.length || 0} הודעות</span>
              </div>
              <button
                className="niv-support-ticket-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirm(ticket.id);
                }}
                title="מחק טיקט"
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ============================================================================
  // RENDER: CHAT VIEW
  // ============================================================================

  const renderChatView = () => {
    if (!selectedTicket) return null;

    return (
      <div className="niv-support-chat">
        {/* Chat Header */}
        <div className="niv-support-chat-header">
          <button className="niv-support-back-btn" onClick={handleBackToList}>
            <ArrowLeft size={20} weight="bold" />
            <span>חזרה</span>
          </button>
          <div className="niv-support-chat-info">
            <h3>{selectedTicket.subject}</h3>
            <div className="niv-support-chat-meta">
              <span>
                <User size={14} />
                {selectedTicket.therapistName}
              </span>
              <span>
                <Buildings size={14} />
                {selectedTicket.clinicName}
              </span>
            </div>
          </div>
          <div className="niv-support-status-select">
            <select
              value={selectedTicket.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={updatingStatus}
              style={{
                borderColor: STATUS_COLORS[selectedTicket.status],
                color: STATUS_COLORS[selectedTicket.status],
              }}
            >
              {STATUS_OPTIONS.filter((o) => o.value !== "all").map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Messages */}
        <div className="niv-support-messages">
          {selectedTicket.messages?.map((msg) => (
            <div
              key={msg.id}
              className={`niv-support-message ${
                msg.senderType === "admin" ? "outgoing" : "incoming"
              }`}
            >
              <div className="niv-support-message-content">
                <div className="niv-support-message-header">
                  <span className="niv-support-message-sender">
                    {msg.senderType === "admin" ? "ניב (מנהל)" : msg.senderName}
                  </span>
                  <span className="niv-support-message-time">
                    {formatTicketDate(msg.createdAt)}
                  </span>
                </div>
                <p>{msg.content}</p>
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <form className="niv-support-input" onSubmit={handleSendMessage}>
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="הקלד תשובה... (Enter לשליחה)"
            rows={2}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="niv-support-send-btn"
          >
            {sending ? (
              <LoaderLoop size="xs" />
            ) : (
              <PaperPlaneTilt size={20} weight="fill" />
            )}
          </button>
        </form>
      </div>
    );
  };

  // ============================================================================
  // RENDER: DELETE CONFIRMATION MODAL
  // ============================================================================

  const renderDeleteConfirm = () => {
    if (!showDeleteConfirm) return null;

    return (
      <div className="niv-support-modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
        <div className="niv-support-modal" onClick={(e) => e.stopPropagation()}>
          <h3>מחיקת טיקט</h3>
          <p>האם אתה בטוח שברצונך למחוק את הטיקט?</p>
          <p className="niv-support-modal-warning">פעולה זו לא ניתנת לביטול</p>
          <div className="niv-support-modal-actions">
            <button
              className="niv-support-modal-btn secondary"
              onClick={() => setShowDeleteConfirm(null)}
            >
              ביטול
            </button>
            <button
              className="niv-support-modal-btn danger"
              onClick={() => handleDeleteTicket(showDeleteConfirm)}
              disabled={deleting}
            >
              {deleting ? <LoaderLoop size="xs" /> : "מחק"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className={`niv-support-page ${embedded ? "is-embedded" : ""}`}>
      {/* Header */}
      <div className="niv-support-header">
        <div className="niv-support-title">
          <Headset size={32} weight="fill" />
          <div>
            <h1>ניהול מרכז תמיכה</h1>
            <p>צפייה וניהול פניות תמיכה מכל המטפלים</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="niv-support-content">
        <div className={`niv-support-panels ${selectedTicket ? "chat-open" : ""}`}>
          {renderTicketsList()}
          {selectedTicket && renderChatView()}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {renderDeleteConfirm()}
    </div>
  );
}

export default NivSupportPage;
